import React, { useEffect, useMemo, useState } from "react";
import {
  BarChartOutlined,
  LineChartOutlined,
  TagOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { Empty, message, Modal, Progress, Spin, Table } from "antd";
import { BillingCouponsStatsModalGlobalStyle } from "./style";
import { fetchCouponUsageStats } from "./lib/api";

const formatStatValue = (value) =>
  value == null || value === "" ? "—" : String(value);

const formatDiscount = (stats) => {
  if (stats?.formatted_total_discount) return stats.formatted_total_discount;
  const value = stats?.total_discount_given;
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatColumnTitle = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

export default function UsageStatsModal({ open, couponId, token, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !couponId || !token) {
      setData(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const result = await fetchCouponUsageStats(couponId, token);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setData(null);
          message.warning(err?.message || "Failed to load usage statistics");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, couponId, token]);

  const coupon = data?.coupon;
  const stats = data?.stats || {};
  const planBreakdown = data?.plan_breakdown || [];
  const usages = data?.usages || [];

  const totalUsage = Number(stats.total_usage ?? 0);
  const usageLimit = stats.usage_limit != null ? Number(stats.usage_limit) : null;
  const usagePercent =
    usageLimit && usageLimit > 0
      ? Math.min(100, Math.round((totalUsage / usageLimit) * 100))
      : 0;
  const remaining =
    usageLimit != null ? Math.max(usageLimit - totalUsage, 0) : null;

  const breakdownColumns = useMemo(
    () =>
      planBreakdown.length
        ? Object.keys(planBreakdown[0]).map((key) => ({
            title: formatColumnTitle(key),
            dataIndex: key,
            key,
            render: (val) => formatStatValue(val),
          }))
        : [],
    [planBreakdown]
  );

  const usageColumns = useMemo(
    () =>
      usages.length
        ? Object.keys(usages[0]).map((key) => ({
            title: formatColumnTitle(key),
            dataIndex: key,
            key,
            render: (val) => formatStatValue(val),
          }))
        : [],
    [usages]
  );

  const statCards = [
    {
      key: "usage",
      label: "Total usage",
      value: formatStatValue(stats.total_usage),
      sub: remaining != null ? `${remaining} remaining` : null,
      icon: <BarChartOutlined />,
    },
    {
      key: "limit",
      label: "Usage limit",
      value: formatStatValue(stats.usage_limit),
      sub: usageLimit != null ? "Redemptions allowed" : null,
      icon: <LineChartOutlined />,
    },
    {
      key: "discount",
      label: "Total discount",
      value: formatDiscount(stats),
      sub: "Given so far",
      icon: <WalletOutlined />,
    },
  ];

  return (
    <>
      <BillingCouponsStatsModalGlobalStyle />
      <Modal
        className="billing-coupons-modal billing-coupons-stats-modal"
        title="Usage statistics"
        open={open}
        onCancel={onClose}
        width={760}
        centered
        destroyOnClose
        footer={[
          <button
            key="close"
            type="button"
            className="btn btn-light fs-7 fw-bold"
            onClick={onClose}
          >
            Close
          </button>,
        ]}
      >
        <Spin spinning={loading}>
          <div className="billing-coupons-stats-modal__inner">
            {coupon ? (
              <div className="billing-coupons-stats-modal__hero">
                <div className="billing-coupons-stats-modal__hero-icon">
                  <TagOutlined />
                </div>
                <div className="billing-coupons-stats-modal__hero-body">
                  <span className="billing-coupons-stats-modal__code">{coupon.code}</span>
                  <div className="billing-coupons-stats-modal__name">{coupon.name}</div>
                </div>
              </div>
            ) : null}

            <div className="billing-coupons-stats-modal__grid">
              {statCards.map((card) => (
                <div key={card.key} className="billing-coupons-stats-modal__card">
                  <div className="billing-coupons-stats-modal__card-top">
                    <span className="billing-coupons-stats-modal__card-label">
                      {card.label}
                    </span>
                    <span className="billing-coupons-stats-modal__card-icon">
                      {card.icon}
                    </span>
                  </div>
                  <div className="billing-coupons-stats-modal__card-value">{card.value}</div>
                  {card.sub ? (
                    <div className="billing-coupons-stats-modal__card-sub">{card.sub}</div>
                  ) : null}
                </div>
              ))}
            </div>

            {usageLimit != null && usageLimit > 0 && (
              <div className="billing-coupons-stats-modal__progress">
                <div className="billing-coupons-stats-modal__progress-head">
                  <span>Redemption progress</span>
                  <span className="billing-coupons-stats-modal__progress-value">
                    {totalUsage} / {usageLimit}
                  </span>
                </div>
                <Progress
                  percent={usagePercent}
                  showInfo={false}
                  strokeColor="#f45b68"
                  trailColor="var(--app-border)"
                  size="small"
                />
              </div>
            )}

            {planBreakdown.length > 0 && (
              <div className="billing-coupons-stats-modal__section">
                <div className="billing-coupons-stats-modal__section-title">
                  Plan breakdown
                </div>
                <Table
                  size="small"
                  rowKey={(_, i) => `plan-${i}`}
                  columns={breakdownColumns}
                  dataSource={planBreakdown}
                  pagination={false}
                  scroll={{ x: true }}
                />
              </div>
            )}

            {usages.length > 0 && (
              <div className="billing-coupons-stats-modal__section">
                <div className="billing-coupons-stats-modal__section-title">
                  Recent usages
                </div>
                <Table
                  size="small"
                  rowKey={(_, i) => `usage-${i}`}
                  columns={usageColumns}
                  dataSource={usages}
                  pagination={false}
                  scroll={{ x: true }}
                />
              </div>
            )}

            {!loading && !planBreakdown.length && !usages.length && (
              <div className="billing-coupons-stats-modal__empty">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <div>
                      <div className="billing-coupons-stats-modal__empty-title">
                        No usage records yet
                      </div>
                      <div className="billing-coupons-stats-modal__empty-text">
                        Redemptions will appear here once customers start using this coupon.
                      </div>
                    </div>
                  }
                />
              </div>
            )}
          </div>
        </Spin>
      </Modal>
    </>
  );
}
